import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, User, CreditCard, LogOut, CheckCircle2, AlertTriangle, RefreshCw, XCircle, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import api, { getErrorMessage } from "@/lib/api";

const PLAN_LABEL = { free: "Free", pro: "Pro Host", pm: "Property Manager" };

const StatusPill = ({ status, canceling }) => {
  if (canceling) return <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--risk-medium-bg))] px-2.5 py-1 text-xs font-medium text-[hsl(var(--risk-medium))]"><AlertTriangle className="h-3 w-3" /> Cancels at period end</span>;
  const map = {
    active: { c: "bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]", I: CheckCircle2, t: "Active" },
    past_due: { c: "bg-[hsl(var(--risk-high-bg))] text-[hsl(var(--risk-high))]", I: AlertTriangle, t: "Payment failed" },
    canceled: { c: "bg-muted text-muted-foreground", I: XCircle, t: "Canceled" },
    none: { c: "bg-muted text-muted-foreground", I: CheckCircle2, t: "Free plan" },
  };
  const cfg = map[status] || map.none;
  const I = cfg.I;
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${cfg.c}`}><I className="h-3 w-3" /> {cfg.t}</span>;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function Settings() {
  const { user, setUser, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState(user?.business_name || "");
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [saving, setSaving] = useState(false);
  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const loadSub = useCallback(async () => {
    try {
      const r = await api.get("/subscription");
      setSub(r.data);
    } finally {
      setSubLoading(false);
    }
  }, []);

  useEffect(() => { loadSub(); }, [loadSub]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/auth/profile", { business_name: businessName, full_name: fullName });
      setUser(res.data);
      toast.success("Profile updated");
    } catch {
      toast.error("Could not update profile");
    } finally { setSaving(false); }
  };

  const cancelSub = async () => {
    setActing(true);
    try {
      const r = await api.post("/subscription/cancel");
      toast.success(`Subscription will end on ${fmtDate(r.data.access_until)}. You keep access until then.`);
      await Promise.all([loadSub(), refreshUser()]);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not cancel."));
    } finally { setActing(false); }
  };

  const resumeSub = async () => {
    setActing(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Subscription resumed.");
      await Promise.all([loadSub(), refreshUser()]);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not resume."));
    } finally { setActing(false); }
  };

  const doLogout = () => { logout(); navigate("/login"); };

  const planId = sub?.plan || user?.plan || "free";
  const isPaid = planId !== "free";

  return (
    <AppShell title="Settings">
      <div className="max-w-2xl space-y-6">
        {/* Profile */}
        <div className="rounded-xl border bg-card p-6">
          <div className="mb-5 flex items-center gap-2"><User className="h-4 w-4 text-primary" /><h3 className="font-display text-base font-semibold">Profile</h3></div>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Email</Label><Input value={user?.email || ""} disabled className="bg-muted/50" /></div>
            <div className="space-y-1.5"><Label>Full name</Label><Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your name" data-testid="settings-fullname-input" /></div>
            <div className="space-y-1.5"><Label>Business name</Label><Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Your business" data-testid="settings-business-input" /></div>
            <Button onClick={save} disabled={saving} data-testid="settings-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
          </div>
        </div>

        {/* Subscription & billing */}
        <div className="rounded-xl border bg-card p-6" data-testid="settings-billing-card">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" /><h3 className="font-display text-base font-semibold">Subscription & billing</h3></div>
            <button onClick={loadSub} className="text-muted-foreground hover:text-foreground" aria-label="Refresh"><RefreshCw className={`h-4 w-4 ${subLoading ? "animate-spin" : ""}`} /></button>
          </div>

          {subLoading ? (
            <div className="flex h-20 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 rounded-lg border bg-secondary/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-display text-lg font-semibold">{PLAN_LABEL[planId]}</p>
                    <StatusPill status={sub?.status} canceling={sub?.cancel_at_period_end} />
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {isPaid ? `$${planId === "pro" ? 19 : 99}/month AUD` : "10 assessments per month · no AI summary"}
                  </p>
                </div>
                <Button variant="outline" onClick={() => navigate("/app/billing")} data-testid="settings-manage-plan-button">
                  Manage billing
                </Button>
              </div>

              {isPaid && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border p-3">
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" /> {sub?.cancel_at_period_end ? "Access until" : "Renews on"}</p>
                    <p className="mt-1 text-sm font-medium" data-testid="settings-renewal-date">{fmtDate(sub?.current_period_end)}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Last payment</p>
                    <p className="mt-1 text-sm font-medium">{sub?.last_payment ? `$${sub.last_payment.amount} ${String(sub.last_payment.currency).toUpperCase()}` : "—"}</p>
                  </div>
                </div>
              )}

              {sub?.status === "past_due" && (
                <div className="flex items-start gap-2 rounded-lg border border-[hsl(var(--risk-high))]/25 bg-[hsl(var(--risk-high-bg))] p-3 text-sm text-[hsl(var(--risk-high))]">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Your last payment failed. Please update your payment method to keep your subscription active.</span>
                </div>
              )}

              {isPaid && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {sub?.cancel_at_period_end ? (
                    <Button variant="outline" onClick={resumeSub} disabled={acting} data-testid="settings-resume-button">
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resume subscription"}
                    </Button>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="text-muted-foreground" data-testid="settings-cancel-button">Cancel subscription</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                          <AlertDialogDescription>
                            You'll keep {PLAN_LABEL[planId]} access until {fmtDate(sub?.current_period_end)}, after which your account reverts to the Free plan. You won't be charged again.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-dialog-keep">Keep subscription</AlertDialogCancel>
                          <AlertDialogAction onClick={cancelSub} data-testid="cancel-dialog-confirm">Cancel subscription</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              )}

              <p className="text-xs text-muted-foreground">Payments are processed securely by Stripe. Prices in AUD.</p>
            </div>
          )}
        </div>

        {/* Logout */}
        <div className="rounded-xl border bg-card p-6">
          <div className="flex items-center justify-between">
            <div><h3 className="font-display text-base font-semibold">Log out</h3><p className="text-xs text-muted-foreground">Sign out of your HostGuard account on this device.</p></div>
            <Button variant="outline" className="gap-2 text-muted-foreground" onClick={doLogout} data-testid="settings-logout-button"><LogOut className="h-4 w-4" /> Log out</Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
